import { DotLegalRadioButton, DotLegalTable, DotLegalTextField, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { Box, CircularProgress, Collapse, IconButton, RadioGroup, TableCell, TableRow, Typography } from "@mui/material";
import { useDocumentVersionTableHook } from "./DocumentVersionTable.hooks";
import { downloadIcon, removeIcon } from "../../common/icons";
import { DocumentVersionViewModel } from "./DocumentVersionTable.types";
import DotLegalFileUploadWrapper from "../../common/components/dotLegalFileUpload/DotLegalFileUploadWrapper";
import UploadProgressBar from "@common/components/uploadProgress/UploadProgressBar";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { EntityType } from "../../common/Common.types";
import { DocumentPreviewable } from "../../common/components/documentPreview/DocumentPreview.types";
import { useDocumentPreviewManager } from "../../common/components/documentPreview/useDocumentPreviewManager";
import React from "react";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection.tsx";

export interface IDocumentVersionTable {
    documentId: string;
    documentsUploading: boolean;
    setDocumentsUploading: (isUploading: boolean) => void;
    entityType: EntityType;
    entityId: string;
    entityName?: string;
    /** A pane is narrow and already frames its content, so the section's border and padding only cost the table width there. */
    withoutSectionFrame?: boolean;
    /** Versions save straight away, so a parent that shows the document beside the table (a pane) needs to know when to refresh. */
    onVersionsChanged?: () => void;
}

function DocumentVersionTable(props: IDocumentVersionTable) {
    const { translateString, translateDate } = useTranslation();
    const { permissions } = useUserContext();
    const {
        handleDownloadClick,
        deleteDocumentVersion,
        document,
        documentationOption,
        linkValidation,
        setDocument,
        setDocumentationOption,
        uploadDocumentVersion,
        uploadLink,
        setDocumentVersionForDeletion,
        documentVersionForDeletion,
        isLoading,
        data,
        isUploading,
        uploadPercentage,
    } = useDocumentVersionTableHook(props);

    const { previewableDocuments, handleDocumentClick } = useDocumentPreviewManager({
        documents: (data ?? []).map((doc) => ({
            documentId: doc.documentId,
            documentVersionId: doc.documentVersionId,
            dataId: doc.dataId,
            fileName: doc.fileName,
            link: doc.link,
            name: doc.fileName || doc.link,
        })),
        entityType: props.entityType,
        entityId: props.entityId,
        entityTitle: props.entityName,
    });

    linkValidation?.check(document);

    const handleRowClick = (row: DocumentVersionViewModel) => {
        const previewable: DocumentPreviewable = {
            documentId: row.documentId,
            documentVersionId: row.documentVersionId,
            dataId: row.dataId,
            fileName: row.fileName,
            link: row.link,
            name: row.fileName || row.link,
        };
        handleDocumentClick(previewable);
    };

    function GetDocumentHeader() {
        const headers = Array<ITableHeader<DocumentVersionViewModel>>();
        headers.push({ property: "fileName", text: translateString("name"), align: "left", showOnMobile: true });
        headers.push({ property: "version", text: translateString("version"), align: "left", showOnMobile: true });
        headers.push({ property: "created", text: translateString("created"), align: "left", showOnMobile: true });
        headers.push({ property: "dataId", text: "", align: "left", showOnMobile: true });

        return headers;
    }

    return (
        <React.Fragment>
            <DotLegalSection noBorder={props.withoutSectionFrame} noPadding={props.withoutSectionFrame}>
                <DotLegalTable
                    headers={GetDocumentHeader()}
                    defaultOrderBy={"version"}
                    defaultOrder={"asc"}
                    data={data ?? []}
                    clickableRows
                    isLoading={isLoading}
                    renderRow={(row, i) => (
                        <TableRow key={i} onClick={() => handleRowClick(row)} sx={{ cursor: "pointer" }}>
                            <TableCell>{row.fileName ?? row.link}</TableCell>
                            <TableCell>{row.version}</TableCell>
                            <TableCell>{translateDate(row.created)}</TableCell>
                            <TableCell width={80} align="right">
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadClick(row.documentId, row.dataId, row.fileName, row.link);
                                    }}
                                >
                                    {downloadIcon}
                                </IconButton>
                                {permissions.canDeleteDoocumentVersion && data && data.length > 1 ? (
                                    documentVersionForDeletion === row.documentVersionId ? (
                                        <IconButton>
                                            <CircularProgress size={15} />
                                        </IconButton>
                                    ) : (
                                        <IconButton
                                            sx={{ opacity: documentVersionForDeletion ? 0.3 : 1 }}
                                            onClick={(e) => {
                                                e.stopPropagation();

                                                if (!documentVersionForDeletion) {
                                                    setDocumentVersionForDeletion(row.documentVersionId);
                                                    deleteDocumentVersion(row.documentId, row.documentVersionId);
                                                }
                                            }}
                                        >
                                            {removeIcon}
                                        </IconButton>
                                    )
                                ) : undefined}
                            </TableCell>
                        </TableRow>
                    )}
                    paginationLabelOf={translateString("labelOf")}
                />
            </DotLegalSection>
            <DotLegalSection noBorder={props.withoutSectionFrame} noPadding={props.withoutSectionFrame} noMargin={props.withoutSectionFrame}>
                <RadioGroup
                    row
                    value={documentationOption}
                    onChange={(e) => {
                        setDocumentationOption(Number((e.target as HTMLInputElement).value));
                    }}
                >
                    <DotLegalRadioButton value={1} labelPlacement="end" label={translateString("uploadDocument")} />
                    <DotLegalRadioButton value={0} labelPlacement="end" label={translateString("insertLink")} />
                </RadioGroup>

                <>
                    <Collapse in={documentationOption === 1}>
                        {
                            <DotLegalFileUploadWrapper
                                files={[document?.fileName!]}
                                onFilesChanged={async (files) => {
                                    await uploadDocumentVersion(files);
                                }}
                                isLoading={props.documentsUploading}
                                pasteEnabled={documentationOption === 1}
                            />
                        }
                        <UploadProgressBar isUploading={isUploading} uploadPercentage={uploadPercentage} />
                    </Collapse>
                    <Collapse in={documentationOption === 0}>
                        <Box sx={{ display: "flex", flexDirection: "column", "& > *": { marginTop: 0 } }}>
                            <DotLegalTextField
                                label={translateString("insertLink")}
                                value={document?.link ?? null}
                                debounce={false}
                                errorText={linkValidation?.getErrorsForDisplay("link")}
                                onChange={(value) => {
                                    let tempModel = { ...document! };
                                    tempModel.link = value;
                                    setDocument(tempModel);
                                }}
                            ></DotLegalTextField>
                            <Typography
                                onClick={uploadLink}
                                sx={(theme) => ({
                                    alignSelf: "end",
                                    fontSize: theme.typography.pxToRem(12),
                                    color: theme.palette.primary.main,
                                    "&:hover": {
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                    },
                                })}
                            >
                                {translateString("insertLink")}
                            </Typography>
                        </Box>
                    </Collapse>
                </>
            </DotLegalSection>
        </React.Fragment>
    );
}

export default DocumentVersionTable;
